import { useEffect, useState } from 'react'
import { getVoices, type VoiceOption } from '@/api/client'
import { loadStoredVoice, storeVoice } from '@/lib/voice-prefs'

/**
 * Interviewer-voice picker state, shared by the pre-interview surfaces
 * (practice preview, invite preview) so both flows behave identically.
 *
 * Loads the backend catalog (YarnGPT entries exist only while
 * YARNGPT_API_KEY is configured), keeps the persisted choice inside it, and
 * resolves what to actually send at join time.
 */
export function useVoiceCatalog() {
  const [voices, setVoices] = useState<VoiceOption[]>([])
  const [voiceId, setVoiceId] = useState(loadStoredVoice)

  // A stored selection that is no longer offered (e.g. YarnGPT key removed)
  // resets to the default instead of getting a 400 at token time.
  useEffect(() => {
    let cancelled = false
    getVoices()
      .then(available => {
        if (cancelled) return
        setVoices(available)
        setVoiceId(current => (available.some(v => v.id === current) ? current : 'default'))
        // Keep the stored choice in sync with a reset above, so a later
        // session doesn't silently revive a voice this page just demoted.
        const stored = loadStoredVoice()
        if (!available.some(v => v.id === stored)) storeVoice('default')
      })
      .catch(() => { /* no picker rather than a broken page when /voices fails */ })
    return () => { cancelled = true }
  }, [])

  const handleVoiceChange = (id: string) => {
    setVoiceId(id)
    storeVoice(id)
  }

  // Join-time guard: /voices may still be in flight (or have failed) when
  // Join is clicked, and a stale stored choice would 400 the join. 'default'
  // is accepted for any session and clears a stale persisted voice
  // server-side.
  const resolveJoinVoice = () => (voices.some(v => v.id === voiceId) ? voiceId : 'default')

  return { voices, voiceId, handleVoiceChange, resolveJoinVoice }
}
